import Tooltip from 'components/layout/tooltip';
import usePtacDesignSystemsSummary from 'hooks/use-ptac-design-systems-summary';
import {
  formatPtacCapacityRange,
  formatPtacEfficiencyRange,
  formatPtacPriceRange,
  hasPtacSummaryRanges,
  PTAC_SYSTEMS_NOT_READY_TOOLTIP
} from 'utils/ptac-systems-summary';

type SummaryRowProps = Readonly<{
  label: string;
  value: string;
}>;

function SummaryRow({ label, value }: SummaryRowProps) {
  return (
    <div className='flex items-center justify-between text-sm'>
      <span className='text-xs font-medium uppercase tracking-wide text-gray-500'>
        {label}
      </span>
      <span className='font-semibold tabular-nums text-gray-800'>{value}</span>
    </div>
  );
}

type PtacSystemsSummaryCardProps = Readonly<{
  onOpenList: () => void;
}>;

export default function PtacSystemsSummaryCard({
  onOpenList
}: PtacSystemsSummaryCardProps) {
  const { ready, isLoading, summary } = usePtacDesignSystemsSummary();

  return (
    <div className='flex shrink-0 flex-col gap-4 rounded-lg border border-gray-200 bg-white p-4'>
      <button
        type='button'
        onClick={onOpenList}
        className='relative flex w-full cursor-pointer flex-col items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-2 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
      >
        <span className='whitespace-nowrap text-xs font-medium uppercase tracking-wide text-gray-500'>
          Valid Systems
        </span>
        <span className='text-lg font-semibold tabular-nums text-gray-800'>
          {summary ? summary.count : '—'}
        </span>
        {!ready && <Tooltip text={PTAC_SYSTEMS_NOT_READY_TOOLTIP} />}
        {isLoading && (
          <span
            aria-label='Refreshing'
            title='Refreshing valid systems'
            className='absolute right-1.5 top-1.5 h-1.5 w-1.5 animate-pulse rounded-full bg-blue-500'
          />
        )}
      </button>
      {isLoading && !summary && (
        <div className='text-center text-xs text-gray-400'>Loading...</div>
      )}
      {summary && hasPtacSummaryRanges(summary) && (
        <div className='flex flex-col gap-1'>
          {summary.priceRange && (
            <SummaryRow
              label='Price'
              value={formatPtacPriceRange(summary.priceRange)}
            />
          )}
          {summary.efficiencyRange && (
            <SummaryRow
              label='Efficiency'
              value={formatPtacEfficiencyRange(summary.efficiencyRange)}
            />
          )}
          {summary.capacityRange && (
            <SummaryRow
              label='Capacity'
              value={formatPtacCapacityRange(summary.capacityRange)}
            />
          )}
        </div>
      )}
    </div>
  );
}
